import { nextTick, type ComponentPublicInstance } from "vue";
import dayjs from "dayjs";
import type { Composer } from "vue-i18n";
import type { TMessageDetail } from "@/api/models/message/message";

const ONE_DAY_S = 24 * 60 * 60;
const ONE_MINUTE_MS = 60 * 1000;

export function getScrollableElement(componentRef: ComponentPublicInstance): HTMLElement | null {
  if (!componentRef) return null;
  return (componentRef as any).$el || (componentRef as any);
}

export async function scrollToBottom(
  element: HTMLElement | ComponentPublicInstance,
  smooth = true
): Promise<void> {
  await nextTick();

  const scrollElement = getScrollableElement(element as ComponentPublicInstance);
  if (scrollElement && (scrollElement as any).scrollTo) {
    (scrollElement as any).scrollTo({
      top: (scrollElement as any).scrollHeight,
      behavior: smooth ? "smooth" : "instant",
    });
  }
}

export async function preserveScrollPosition(
  element: HTMLElement | ComponentPublicInstance,
  callback: () => Promise<void>
): Promise<void> {
  const scrollElement = getScrollableElement(element as ComponentPublicInstance);
  if (!scrollElement) {
    await callback();
    return;
  }

  const scrollHeightBefore = scrollElement.scrollHeight;
  const scrollTopBefore = scrollElement.scrollTop;

  await callback();
  await nextTick();

  if (scrollElement) {
    const scrollHeightAfter = scrollElement.scrollHeight;
    const heightDifference = scrollHeightAfter - scrollHeightBefore;
    scrollElement.scrollTop = scrollTopBefore + heightDifference;
  }
}

export function getRelativeTimeFormat(
  messageDate: Date,
  t: Composer["t"],
  nowTs: number = Date.now()
): string {
  const now = dayjs(nowTs);
  const messageTime = dayjs(messageDate);

  const diffInSeconds = now.diff(messageTime, "second");

  if (diffInSeconds < 10) {
    return t("models.message.now");
  }
  if (diffInSeconds < 60) {
    return t("models.message.secondsAgo", { count: diffInSeconds });
  }
  if (diffInSeconds < 3600) {
    return t("models.message.minutesAgo", { count: Math.floor(diffInSeconds / 60) });
  }
  if (diffInSeconds < ONE_DAY_S) {
    return t("models.message.hoursAgo", { count: Math.floor(diffInSeconds / 3600) });
  }
  // TODO add this setting from settings page
  return messageTime.format("MMMM DD, YYYY hh:mm A");
}

export function isNewMessageGroup(
  currentMessage: TMessageDetail,
  previousMessage?: TMessageDetail
): boolean {
  if (!previousMessage) return true;

  if (currentMessage.sender !== previousMessage.sender) return true;

  return isTimeGapOverMinute(currentMessage.created_at, previousMessage.created_at);
}

export function isTimeGapOverMinute(
  currentMessageTime?: Date,
  previousMessageTime?: Date
): boolean {
  const cmt = dayjs(currentMessageTime).valueOf();
  const pmt = dayjs(previousMessageTime).valueOf();
  const timeDiff = cmt - pmt;

  return timeDiff > ONE_MINUTE_MS;
}
